import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { WifiOff, Wifi, Cloud, CloudOff } from 'lucide-react';
import { offlineStorage } from '@/lib/offlineStorage';

export const OfflineIndicator = ({ language = 'en' }) => {
  const [isOnline, setIsOnline] = useState(true);
  const [showBanner, setShowBanner] = useState(false);

  const texts = {
    pt: {
      offline: 'Você está offline',
      offlineDesc: 'Algumas funcionalidades podem estar limitadas',
      online: 'Você está online novamente!',
      savedOffline: 'Seus favoritos estão salvos para acesso offline'
    },
    en: {
      offline: 'You are offline',
      offlineDesc: 'Some features may be limited',
      online: 'You are back online!',
      savedOffline: 'Your favorites are saved for offline access'
    }
  };

  const t = texts[language] || texts.en;

  useEffect(() => {
    // Check initial status
    setIsOnline(navigator.onLine);

    const handleOnline = () => {
      setIsOnline(true);
      setShowBanner(true);
      setTimeout(() => setShowBanner(false), 3000);
    };

    const handleOffline = () => {
      setIsOnline(false);
      setShowBanner(true);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return (
    <AnimatePresence>
      {showBanner && (
        <motion.div
          initial={{ y: -100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -100, opacity: 0 }}
          transition={{ type: 'spring', stiffness: 300, damping: 30 }}
          className={`fixed top-0 left-0 right-0 z-50 p-3 flex items-center justify-center gap-3 ${
            isOnline
              ? 'bg-gradient-to-r from-green-500 to-emerald-500 text-white'
              : 'bg-gradient-to-r from-orange-500 to-amber-500 text-white'
          }`}
        >
          {isOnline ? (
            <>
              <Wifi className="w-5 h-5" />
              <span className="font-medium">{t.online}</span>
            </>
          ) : (
            <>
              <WifiOff className="w-5 h-5" />
              <div className="text-center">
                <span className="font-medium">{t.offline}</span>
                <span className="text-white/80 text-sm ml-2">- {t.offlineDesc}</span>
              </div>
            </>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// Offline badge for property cards
export const OfflineBadge = ({ language = 'en' }) => {
  const text = language === 'pt' ? 'Disponível Offline' : 'Available Offline';
  
  return (
    <div className="flex items-center gap-1 bg-green-100 text-green-700 text-xs px-2 py-1 rounded-full">
      <Cloud className="w-3 h-3" />
      <span>{text}</span>
    </div>
  );
};

export default OfflineIndicator;
